import { useEffect, useState } from "react";
import { MDX } from "./mdx/tokens";
import { Check, GraduationCap, Landmark, Sparkles, User } from "lucide-react";
import { api } from "@/lib/api";
import CurrencyToggle from "./CurrencyToggle";
import { Reveal } from "@/components/Reveal";

/**
 * PricingSectionMdx — Section 7 (Multi-Currency, 4-Tier).
 * Tiers (Part 4 rebuild): Kimani (free) · Obondo Standard/Growth/Scale —
 * metered per-active-user pricing mirroring the Part 1 MAU billing engine
 * (services/mau_engine.py::TIERS). Institutional/API access is by
 * application (`/apply`), reviewed before onboarding.
 *
 * All paid tier prices are held in USD (canonical) and converted to the
 * user's selected currency (USD · KES · EUR) using live FX from
 * /api/market/ticker/USDKES + EURKES with hard-fallback rates.
 */

const FX_FALLBACK = { KES_per_USD: 129.5, EUR_per_USD: 0.92 };

const TIERS = [
    {
        id: "kimani",
        name: "Kimani",
        badge: "Free forever",
        badgeTone: "gray",
        priceUsd: 0,
        priceNote: null,
        icon: User,
        subtitle: "Free for anyone learning to trade",
        features: [
            "Practice buying and selling with virtual money",
            "Helps us improve the app, only if you say yes (deleted after 7 days)",
            "Real prices from 21 stock markets in 33 African countries",
            "Free step-by-step helper to get you started",
            "No card required · free forever",
        ],
        cta:     { label: "Sign Up Free", to: "/register", variant: "outline" },
    },
    {
        id: "obondo-standard",
        name: "Obondo Standard",
        badge: "✦ 7-Day Free Trial",
        badgeTone: "green",
        priceUsd: 4.99,
        priceUnit: true,
        priceNote: "1–200 monthly active users",
        icon: Sparkles,
        subtitle: "Metered API access — pay only for active users",
        features: [
            "Pay per Monthly Active User (MAU), not per seat",
            "1,000 API requests/MAU included · $0.005/request after",
            "Prepaid slots + real-time cash buffer dashboard",
            "Self-service API keys — no approval wait",
            "7-day free trial, then billed monthly",
        ],
        cta:     { label: "Apply for API Access", to: "/apply", variant: "solid", action: "link" },
        featured: true,
    },
    {
        id: "obondo-growth",
        name: "Obondo Growth",
        badge: "✦ 7-Day Free Trial",
        badgeTone: "green",
        priceUsd: 4.19,
        priceUnit: true,
        priceNote: "201–500 monthly active users",
        icon: GraduationCap,
        subtitle: "A better per-user rate as your usage scales",
        features: [
            "Everything in Standard, plus:",
            "Lower per-MAU rate at 201–500 active users",
            "Priority fair-use overlap monitoring",
            "Auto-provisioning + auto-top-up controls",
            "Dedicated onboarding support",
        ],
        cta:     { label: "Apply for API Access", to: "/apply", variant: "solid", action: "link" },
    },
    {
        id: "obondo-scale",
        name: "Obondo Scale",
        badge: "✦ 7-Day Free Trial",
        badgeTone: "green",
        priceUsd: 3.49,
        priceUnit: true,
        priceNote: "501+ monthly active users",
        icon: Landmark,
        subtitle: "Best rate at volume, with dedicated support",
        features: [
            "Everything in Growth, plus:",
            "Best per-MAU rate at 501+ active users",
            "Dedicated account manager",
            "Custom SLA & compliance review",
            "Volume-based prepaid slot discounts",
        ],
        cta:     { label: "Apply for API Access", to: "/apply", variant: "solid", action: "link" },
    },
];

export default function PricingSectionMdx({ isDark, onStartTrial }) {
    const bg = isDark ? "bg-[#090D16]" : "bg-white";
    const text = isDark ? "text-[#F9FAFB]" : "text-[#090D16]";
    const dim = isDark ? "text-[#9CA3AF]" : "text-[#6B7280]";
    const cardBg = isDark ? "bg-[#0F1524]" : "bg-white";
    const cardBrd = isDark ? "border-[#1F2937]" : "border-[#E5E7EB]";

    const [currency, setCurrency] = useState(() => detectDefaultCurrency());
    const [fx, setFx] = useState(FX_FALLBACK);

    useEffect(() => {
        (async () => {
            try {
                const [u, e] = await Promise.all([
                    api.get("/market/ticker/USDKES").catch(() => null),
                    api.get("/market/ticker/EURKES").catch(() => null),
                ]);
                const usdKes = u?.data?.price;
                const eurKes = e?.data?.price;
                setFx({
                    KES_per_USD: usdKes || FX_FALLBACK.KES_per_USD,
                    EUR_per_USD: usdKes && eurKes ? usdKes / eurKes : FX_FALLBACK.EUR_per_USD,
                });
            } catch { /* fallback rates stay */ }
        })();
    }, []);

    return (
        <section
            className={`${bg} ${text} pt-16 md:pt-24 pb-10 md:pb-16 border-t ${cardBrd}`}
            data-testid="pricing-mdx"
            id="pricing"
        >
            <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <div>
                        <div className="font-inter text-xs font-bold uppercase tracking-widest mb-3" style={{ color: MDX.blueLow }}>
                            Simple Pricing
                        </div>
                        <h2 className="font-inter font-medium leading-tight" style={{ fontSize: "28px" }}>
                            Free forever. Or 7 days free to try Obondo.
                        </h2>
                    </div>
                    <CurrencyToggle value={currency} onChange={setCurrency} isDark={isDark} testid="pricing-currency-toggle" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="pricing-grid">
                    {TIERS.map((t, i) => (
                        <Reveal key={t.id} delay={i * 0.08} className="h-full">
                            <TierCard
                                tier={t}
                                currency={currency}
                                fx={fx}
                                isDark={isDark}
                                cardBg={cardBg}
                                cardBrd={cardBrd}
                                dim={dim}
                                onStartTrial={onStartTrial}
                            />
                        </Reveal>
                    ))}
                </div>

                {/* Subtle divider so the transition into the compliance footer
                    isn't an abrupt flat line. */}
                <div className="mt-10 md:mt-14 flex items-center justify-center gap-3" data-testid="pricing-section-divider" aria-hidden="true">
                    <span className="h-px flex-1 max-w-[160px]" style={{ background: `linear-gradient(to right, transparent, ${cardBrd.includes("1F2937") ? "#1F2937" : "#E5E7EB"})` }} />
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: MDX.greenCta }} />
                    <span className="h-px flex-1 max-w-[160px]" style={{ background: `linear-gradient(to left, transparent, ${cardBrd.includes("1F2937") ? "#1F2937" : "#E5E7EB"})` }} />
                </div>
            </div>
        </section>
    );
}

/* ── Tier card ─────────────────────────────────────────────── */
function TierCard({ tier, currency, fx, isDark, cardBg, cardBrd, dim, onStartTrial }) {
    const Icon = tier.icon;
    const isFree = tier.priceUsd === 0;
    const priceLine = isFree ? "Free" : fmtPrice(tier.priceUsd, currency, fx);
    const localApprox = isFree ? null : fmtSecondaryPrice(tier.priceUsd, currency, fx);
    const isFeatured = tier.featured;
    const unitSuffix = tier.priceUnit ? "/user/mo" : "/mo";

    const borderStyle = isFeatured
        ? { borderColor: MDX.blueLow, boxShadow: "0 8px 32px rgba(146,37,41,0.15)" }
        : {};

    return (
        <div
            className={`relative h-full rounded-xl border p-5 flex flex-col transition-all duration-200 hover:-translate-y-1 hover:shadow-lg ${cardBg} ${!isFeatured ? cardBrd : "border-2"}`}
            style={borderStyle}
            data-testid={`tier-${tier.id}`}
        >
            {tier.badge && (
                <div
                    className="absolute -top-3 left-5 font-inter text-[10px] font-bold uppercase tracking-widest rounded-full px-2.5 py-1"
                    style={
                        tier.badgeTone === "green"
                            ? { background: MDX.greenCtaSolid, color: "#fff" }
                            : { background: isDark ? "#1F2937" : "#F3F4F6", color: isDark ? "#9CA3AF" : "#6B7280" }
                    }
                >
                    {tier.badge}
                </div>
            )}

            <div className="flex items-center gap-2 mt-1 mb-2">
                <Icon className="h-4 w-4" style={{ color: isFeatured ? MDX.blueLow : MDX.grayMid }} />
                <div className="font-inter font-bold text-[15px] leading-tight">{tier.name}</div>
            </div>
            <p className={`font-roboto text-[13px] ${dim} mb-4`}>{tier.subtitle}</p>

            {/* Price block */}
            <div className="mb-4" data-testid={`tier-${tier.id}-price`}>
                <div className="font-inter font-bold" style={{ fontSize: "28px", lineHeight: 1 }}>
                    {priceLine}
                    {!isFree && <span className={`text-base font-medium ${dim}`}> {unitSuffix}</span>}
                </div>
                {(tier.priceNote || localApprox) && (
                    <div className={`font-roboto text-[12px] mt-1.5 ${dim}`}>
                        {tier.priceNote && <span>{tier.priceNote}</span>}
                        {localApprox && (
                            <span className="opacity-80">{tier.priceNote ? " · " : ""}{localApprox}</span>
                        )}
                    </div>
                )}
            </div>

            {/* Features */}
            <ul className="space-y-1.5 flex-1 mb-5">
                {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 font-roboto text-[14px] leading-snug">
                        <Check className="h-3.5 w-3.5 mt-1 shrink-0" style={{ color: isFeatured ? MDX.blueLow : MDX.greenCta }} />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>

            {/* CTA */}
            {tier.cta.variant === "solid" && tier.cta.action === "link" ? (
                <a
                    href={tier.cta.to}
                    data-testid={`tier-${tier.id}-cta`}
                    style={{ background: MDX.greenCtaSolid }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = MDX.greenHov)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = MDX.greenCta)}
                    className="inline-flex items-center justify-center w-full min-h-[44px] rounded-md font-inter font-bold uppercase tracking-wider text-[17px] py-3 text-white transition-colors"
                >
                    {tier.cta.label}
                </a>
            ) : tier.cta.variant === "solid" ? (
                <button
                    onClick={onStartTrial}
                    data-testid={`tier-${tier.id}-cta`}
                    style={{ background: MDX.greenCtaSolid }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = MDX.greenHov)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = MDX.greenCta)}
                    className="inline-flex items-center justify-center w-full min-h-[44px] rounded-md font-inter font-bold uppercase tracking-wider text-[17px] py-3 text-white transition-colors"
                >
                    {tier.cta.label}
                </button>
            ) : (
                <a
                    href={tier.cta.to}
                    data-testid={`tier-${tier.id}-cta`}
                    className="inline-flex items-center justify-center w-full rounded-md border-2 font-inter font-bold uppercase tracking-wider text-[17px] py-3 transition-colors"
                    style={{ borderColor: MDX.greenCta, color: MDX.greenCta }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = MDX.greenCta; e.currentTarget.style.color = "#fff"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = MDX.greenCta; }}
                >
                    {tier.cta.label}
                </a>
            )}
        </div>
    );
}

/* ── Locale-based currency default ───────────────────────────
 * Reads navigator.language + timezone to guess the visitor's currency.
 * KES for Kenyan English / Africa/Nairobi, EUR for EU locales, USD otherwise.
 * Runs synchronously in useState initializer so first paint is already right.
 */
function detectDefaultCurrency() {
    if (typeof window === "undefined" || typeof navigator === "undefined") return "USD";
    try {
        const lang = (navigator.language || "").toLowerCase();
        const langs = (navigator.languages || []).map((l) => l.toLowerCase()).join(",");
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
        const all = `${lang},${langs},${tz.toLowerCase()}`;

        // Kenya · East Africa neighbours that transact in KES for cross-border SaaS.
        if (
            /-ke\b|_ke\b|africa\/nairobi/.test(all) ||
            /sw-|-tz\b|-ug\b|-rw\b|africa\/dar_es_salaam|africa\/kampala|africa\/kigali/.test(all)
        ) return "KES";

        // Eurozone locales — 20 EU/EEA countries + French African neighbours.
        if (/-(fr|de|es|it|pt|nl|be|at|ie|fi|gr|lu|mt|cy|ee|lv|lt|sk|si|hr)\b/.test(all)) return "EUR";
        if (/europe\/(paris|berlin|madrid|rome|amsterdam|brussels|vienna|lisbon|dublin|helsinki|athens)/.test(all)) return "EUR";

        return "USD";
    } catch {
        return "USD";
    }
}

/* ── Currency formatters ─────────────────────────────────────── */
function fmtPrice(usd_amount, currency, fx) {
    if (!usd_amount || usd_amount <= 0) return "Free";
    if (currency === "USD") return `$${usd_amount}`;
    if (currency === "EUR") return `€${Math.round(usd_amount * (fx?.EUR_per_USD || 0.92))}`;
    const kes = usd_amount * (fx?.KES_per_USD || 129.5);
    return `KES ${Math.round(kes).toLocaleString()}`;
}

function fmtSecondaryPrice(usd_amount, currency, fx) {
    if (!usd_amount || usd_amount <= 0) return null;
    if (currency === "KES") return `≈ $${usd_amount}`;
    const kes = usd_amount * (fx?.KES_per_USD || 129.5);
    if (currency === "EUR") return `≈ KES ${Math.round(kes).toLocaleString()}`;
    return `≈ KES ${Math.round(kes).toLocaleString()}`;
}
